import { resetAnalytics } from '../analytics'
import { setUnauthorizedHandler } from '../api/client'
import { authReducer, initialAuthState } from './reducer'
import type { AuthAction, AuthState } from './types'
import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import type { Dispatch, ReactNode } from 'react'

const AUTH_STORAGE_KEY = 'hs_portal_auth'
const AUTH_STORAGE_VALUE = 'authenticated'

const readPersistedPhase = (): AuthState => {
  try {
    if (window.localStorage.getItem(AUTH_STORAGE_KEY) === AUTH_STORAGE_VALUE) {
      return { phase: 'authenticated' }
    }
  } catch {
    /* localStorage may be unavailable in private mode; treat as anonymous */
  }
  return initialAuthState
}

const writePersistedPhase = (phase: AuthState['phase']) => {
  try {
    if (phase === 'authenticated') {
      window.localStorage.setItem(AUTH_STORAGE_KEY, AUTH_STORAGE_VALUE)
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  } catch {
    /* no-op; persistence is a best-effort optimization */
  }
}

interface AuthContextValue {
  state: AuthState
  dispatch: Dispatch<AuthAction>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
  /** Override initial state for tests. */
  initialState?: AuthState
}

export const AuthProvider = ({ children, initialState }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(
    authReducer,
    initialState ?? readPersistedPhase()
  )

  useEffect(() => {
    writePersistedPhase(state.phase)
  }, [state.phase])

  useEffect(() => {
    setUnauthorizedHandler(() => {
      dispatch({ type: 'logged_out', reason: 'revoked' })
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_STORAGE_KEY) return
      if (event.newValue === AUTH_STORAGE_VALUE) {
        dispatch({ type: 'logged_in' })
      } else {
        dispatch({ type: 'logged_out' })
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const queryClient = useQueryClient()
  const previousPhase = useRef(state.phase)
  useEffect(() => {
    // Drop cached portal data on logout so a later sign-in (possibly as a
    // different email) never flashes the previous session's conversations.
    if (
      previousPhase.current === 'authenticated' &&
      state.phase !== 'authenticated'
    ) {
      queryClient.clear()
      // Drop the Mixpanel identity too, so events on a shared device aren't
      // tied to the previous customer. Covers manual, 401, and cross-tab logout.
      resetAnalytics()
    }
    previousPhase.current = state.phase
  }, [state.phase, queryClient])

  const value = useMemo(() => ({ state, dispatch }), [state])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return context
}
